import {getCountry, Locale, useConfig} from '@aboutyou/config';
import {GetOrderSuccessPageResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/order_success_page/v1/service.types';
import {useCookie} from '@aboutyou/storage';
import {useEffect, useMemo} from 'react';

const BAMBUSER_LAST_INTERACTION_TIME = '_bamls_lits'; // set by an external script and lives to 30d

// Define a clearer type for the tracking data
interface TrackingData {
  orderProductIds: string;
  orderValue: string;
  orderId: string;
  currency: string;
  country: string;
}

const haveDaysFromDatePassed = (date: Date, days: number): boolean => {
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= days;
};

const extractTrackingData = (
  items: GetOrderSuccessPageResponse[],
  locale: Locale,
): TrackingData | undefined => {
  const orderDetails = items.find(
    item => item.section.type?.$case === 'orderDetails',
  );

  if (!orderDetails || orderDetails.section.type?.$case !== 'orderDetails') {
    return undefined;
  }

  const sectionOrderDetails = orderDetails.section.type.orderDetails;

  const productIds = sectionOrderDetails.packages.packages
    .flatMap(pkg => pkg.items || [])
    .map(item => item.productTracker.productId);

  return {
    orderProductIds: productIds.join(','),
    orderValue: `${(sectionOrderDetails.total.amount.amount / 100).toFixed(2)}`,
    orderId: sectionOrderDetails.orderId,
    currency: sectionOrderDetails.total.amount.currencyCode,
    country: getCountry(locale),
  };
};

const checkShouldTrackDataBasedOnPlayerCookie = ({
  cookieValue,
}: {
  cookieValue: string | undefined;
}): boolean => {
  const DAYS_SINCE_LAST_PLAYER_INTERACTION = 2;

  // cookie is not set - the player was not interacted
  if (!cookieValue) {
    return false;
  }

  const dateOfLastInteraction = new Date(cookieValue);

  return !haveDaysFromDatePassed(
    dateOfLastInteraction,
    DAYS_SINCE_LAST_PLAYER_INTERACTION,
  );
};

export const useLiveShoppingPurchaseTracking = (
  items: GetOrderSuccessPageResponse[],
): void => {
  const {locale} = useConfig();

  const [lastInteractionTime, ,] = useCookie<string | undefined>({
    cookieName: BAMBUSER_LAST_INTERACTION_TIME,
    defaultValue: undefined,
    transformRawValue: value => value,
  });

  const shouldTrackData = checkShouldTrackDataBasedOnPlayerCookie({
    cookieValue: lastInteractionTime,
  });

  const trackingData = useMemo(
    () => extractTrackingData(items, locale),
    [items, locale],
  );

  useEffect(() => {
    if (window._bambuser && shouldTrackData && trackingData?.orderId) {
      window._bambuser.collect({
        event: 'purchase',
        ...trackingData,
      });
    }
  }, [shouldTrackData, trackingData]);
};
